import React, { useEffect, useState } from 'react';
import { Upload, Database, AlertCircle } from 'lucide-react';
import GLViewer from './GLViewer';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';

const GLPage = () => {
  const { clientId, clientName, hasActiveClient } = useClient();
  const [glList, setGlList] = useState([]);
  const [selectedGl, setSelectedGl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadGLList = async () => {
    if (!clientId) return;
    try {
      const res = await fetch(`${API}/gl/list?limit=20&tenant_id=${clientId}`);
      if (res.ok) {
        const data = await res.json();
        setGlList(data.gl || []);
        if (!selectedGl && data.gl && data.gl.length > 0) {
          setSelectedGl(data.gl[0].gl_id);
        }
      }
    } catch (e) {
      console.error('Failed to load GL list:', e);
    }
  };

  useEffect(() => {
    loadGLList();
  }, [clientId]);

  const pollJobStatus = async (jobId, maxAttempts = 120) => {
    for (let i = 0; i < maxAttempts; i++) {
      const res = await fetch(`${API}/gl-upload/job-status?job_id=${encodeURIComponent(jobId)}`);
      if (!res.ok) {
        throw new Error(`Status check failed: HTTP ${res.status}`);
      }

      const job = await res.json();
      if (job.status === 'queued') {
        setUploadProgress(25);
      } else if (job.status === 'processing') {
        setUploadProgress(65);
      } else if (job.status === 'completed') {
        setUploadProgress(100);
        return job.result || job;
      } else if (job.status === 'error') {
        throw new Error(job.error || 'GL processing failed');
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    throw new Error('GL processing timed out. Please check the GL list for results.');
  };

  const handleDirectUpload = async (file, sheetName) => {
    if (!file) return;
    if (!clientId) {
      setError('Please select a client before uploading');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('tenant_id', clientId);
      formData.append('file', file);
      formData.append('sheet_name', sheetName || '2024 GL Detail');

      const res = await fetch(`${API}/gl-upload/direct`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Upload failed: HTTP ${res.status}`);
      }

      const data = await res.json();
      let finalData = data;

      if (data.job_id) {
        setUploadProgress(15);
        finalData = await pollJobStatus(data.job_id);
      } else {
        setUploadProgress(100);
      }

      const processedRows =
        finalData?.row_count ??
        finalData?.total_rows ??
        finalData?.rows_processed ??
        null;

      setSuccessMessage(
        `Successfully uploaded GL. Processed ${processedRows != null ? Number(processedRows).toLocaleString() : 'N/A'} rows.`
      );

      await loadGLList();
      if (finalData?.gl_id) {
        setSelectedGl(finalData.gl_id);
      }

      setTimeout(() => {
        setUploadProgress(0);
        setSuccessMessage(null);
      }, 5000);
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* No Client Selected Warning */}
      {!hasActiveClient() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <div>
            <p className="font-medium text-yellow-800">No Client Selected</p>
            <p className="text-sm text-yellow-700">Please select a client from the sidebar to view and upload GL data.</p>
          </div>
        </div>
      )}

      {/* Active Client Banner */}
      {hasActiveClient() && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
          <Database className="w-4 h-4 text-blue-600" />
          <span className="text-sm text-blue-800">
            Viewing GL data for: <strong>{clientName}</strong>
          </span>
        </div>
      )}

      {/* Page Header */}
      <div className="reconcile-header" style={{ marginBottom: '2rem', padding: '1.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
        <div className="reconcile-title">General Ledger</div>
        <div className="reconcile-meta">
          <div className="reconcile-meta-item">
            <Database style={{ width: '1rem', height: '1rem' }} />
            <span className="reconcile-meta-label">Data Source:</span>
            <span>GL Upload</span>
          </div>
          <div className="reconcile-meta-item">
            <span className="reconcile-meta-label">Status:</span>
            <span className="pro-badge pro-badge-success">Active</span>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="pro-card" style={{ marginBottom: '2rem' }}>
        <div className="pro-card-header">
          <h3 className="pro-card-title">Upload General Ledger</h3>
        </div>
        <div className="pro-card-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', display: 'block', marginBottom: '0.5rem' }}>
                Select Excel File
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                id="gl-file-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // Store file reference for upload button
                    e.target.dataset.selectedFile = file.name;
                  }
                }}
                disabled={uploading}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--gray-300)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <button
              className="pro-btn pro-btn-primary"
              disabled={uploading}
              style={{ height: 'fit-content' }}
              onClick={() => {
                const fileInput = document.getElementById('gl-file-input');
                const file = fileInput?.files?.[0];
                if (file) {
                  handleDirectUpload(file, '2024 GL Detail');
                } else {
                  alert('Please select a file first');
                }
              }}
            >
              <Upload style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
              {uploading ? 'Uploading...' : 'Upload GL'}
            </button>
          </div>

          {uploading && uploadProgress > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ width: '100%', height: '0.5rem', background: 'var(--gray-200)', borderRadius: '9999px', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    background: 'var(--accent-orange)',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}

          {error && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'start', gap: '0.5rem' }}>
              <AlertCircle style={{ width: '1rem', height: '1rem', color: '#991B1B', flexShrink: 0, marginTop: '0.125rem' }} />
              <p style={{ fontSize: '0.875rem', color: '#991B1B' }}>{error}</p>
            </div>
          )}
          
          {successMessage && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: '#D1FAE5', border: '1px solid #10B981', borderRadius: 'var(--radius-md)', color: '#065F46', fontSize: '0.875rem', fontWeight: '500' }}>
              {successMessage}
            </div>
          )}
        </div>
      </div>

      {/* GL Selection */}
      <div className="pro-card" style={{ marginBottom: '2rem' }}>
        <div className="pro-card-header">
          <h3 className="pro-card-title">Select GL Dataset</h3>
        </div>
        <div className="pro-card-content">
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
              GL Dataset:
            </label>
            <select
              value={selectedGl}
              onChange={(e) => setSelectedGl(e.target.value)}
              className="pro-input"
              style={{ maxWidth: '500px' }}
            >
              <option value="">-- Select a GL dataset --</option>
              {glList.map((gl, i) => (
                <option key={i} value={gl.gl_id}>
                  {gl.filename || gl.sheet_name || 'GL'} • {gl.gl_id} • {gl.row_count?.toLocaleString() || 0} rows
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* GL Viewer */}
      {selectedGl ? (
        <GLViewer glId={selectedGl} />
      ) : (
        <div className="pro-card">
          <div className="pro-card-content" style={{ textAlign: 'center', padding: '3rem' }}>
            <Database style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: 'var(--gray-300)' }} />
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
              No GL dataset selected. Please upload or select a GL dataset to view.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GLPage;

